import { X } from "lucide-react";
import * as React from "react";

import styles from "./TagInput.module.css";

export interface TagInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange" | "defaultValue"
  > {
  /** Tags to seed the component with on mount. Uncontrolled: later changes to this prop are ignored. */
  initialTags: string[];
  /** Called with the full updated tag list whenever a tag is added or removed. */
  onChange: (tags: string[]) => void;
  /**
   * Placeholder shown in the draft input when there are no tags yet.
   * @default "Add tags…"
   */
  placeholder?: string;
}

/**
 * A tag/chip input primitive. Renders committed tags as removable pills plus
 * a draft text field for adding new ones — Enter or comma commits the draft,
 * Backspace on an empty draft removes the last tag.
 *
 * Uncontrolled: seed it with `initialTags` and read live updates via
 * `onChange`. Later changes to `initialTags` after mount are ignored — this
 * matches how the two current call sites use it (react-hook-form's
 * `Controller`, seeding once from the field's default value).
 *
 * The forwarded ref attaches to the draft `<input>`, and any additional
 * native input props (`id`, `aria-label`, `aria-describedby`, `disabled`,
 * `name`, etc.) are passed through.
 *
 * @example
 * ```tsx
 * <TagInput initialTags={["urban", "night"]} onChange={setTags} />
 * ```
 */
export const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  function TagInput(
    { initialTags, onChange, placeholder = "Add tags…", ...rest },
    ref,
  ) {
    const [tags, setTags] = React.useState(initialTags);
    const [draft, setDraft] = React.useState("");
    const [announcement, setAnnouncement] = React.useState("");

    const commitDraft = () => {
      const tag = draft.trim().replace(/,$/, "");
      if (!tag) {
        setDraft("");
        return;
      }
      if (tags.includes(tag)) {
        setAnnouncement(`"${tag}" is already added`);
        setDraft("");
        return;
      }
      const next = [...tags, tag];
      setTags(next);
      onChange(next);
      setAnnouncement(`Added "${tag}"`);
      setDraft("");
    };

    const removeTag = (tag: string) => {
      const next = tags.filter((t) => t !== tag);
      setTags(next);
      onChange(next);
      setAnnouncement(`Removed "${tag}"`);
    };

    return (
      <div
        className={styles.tagInput}
        role="group"
        aria-label="Tags"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            (e.currentTarget as HTMLElement).querySelector("input")?.focus();
          }
        }}
      >
        <ul className={styles.pillList}>
          {tags.map((t) => (
            <li key={t} className={styles.pill}>
              {t}
              <button
                type="button"
                className={styles.pillRemove}
                onClick={() => removeTag(t)}
                aria-label={`Remove ${t}`}
                tabIndex={-1}
              >
                <X size={10} />
              </button>
            </li>
          ))}
        </ul>
        <input
          {...rest}
          ref={ref}
          className={styles.draft}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commitDraft();
            } else if (e.key === "Backspace" && !draft && tags.length) {
              const removed = tags[tags.length - 1];
              const next = tags.slice(0, -1);
              setTags(next);
              onChange(next);
              setAnnouncement(`Removed "${removed}"`);
            }
          }}
          onBlur={(e) => {
            if (
              !e.currentTarget.parentElement?.contains(e.relatedTarget as Node)
            ) {
              commitDraft();
            }
          }}
          placeholder={tags.length ? "" : placeholder}
        />
        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>
      </div>
    );
  },
);
